// creating auth context
import React, { createContext, useEffect, useState } from 'react';
import { useUserStore } from '../models'
import { SERVER_URL } from '../constants'

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(null);
  const {setUser} = useUserStore();
  const [loading, setLoading] = useState(!!localStorage.getItem('token'));

  useEffect(() => {

    const token = localStorage.getItem('token')

    const checkAuth = async () => {
      setLoading(true)
      const response = await fetch(`${SERVER_URL}/check`, {
        headers: {
          Authorization: 'Bearer ' + localStorage.getItem('token') || '',
        }
      });

      if (response.ok) {
        const { user, token: newToken } = await response.json();

        localStorage.setItem('token', newToken)
        localStorage.setItem('user', JSON.stringify(user));
        setUser(user);
        setAuth(newToken);
      } else {
        localStorage.removeItem('token')
      }

      setLoading(false);
    }

    if (token) {
      checkAuth()
    }
  }, []);

  const checkPhone = async (phone) => {
    const res = await fetch(`${SERVER_URL}/auth/checkphone`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ phone }),
    });
    const data = await res.json();
    return data;
  };

  const checkUsername = async (username) => {
    const res = await fetch(`${SERVER_URL}/auth/checkname`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username }),
    });
    const data = await res.json();
    return data;
  };

  const checkEmail = async (email) => {
    const res = await fetch(`${SERVER_URL}/auth/checkemail`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });
    const data = await res.json();
    return data;
  };

  const signin = async (email, password) => {
    setLoading(true);
    const res = await fetch(`${SERVER_URL}/auth/signin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (data.error) {
      setLoading(false);
      return data;
    } else {
      if (data.token && data.result) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.result));
        setUser(data.user);
        setAuth(data.token);
      }
    }
    setLoading(false);
    return data;
  };

  const signup = async (name, email, password, code, phone) => {
    setLoading(true);
    const res = await fetch(`${SERVER_URL}/auth/signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name, email, password, code, phone }),
    });
    const data = await res.json();
    if (data.error) {
      setLoading(false);
      return data;
    }
    if (data) {
      if (data.token && data.result) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.result));
        setUser(data.user);
        setAuth(data.token);
      }
    }
    setLoading(false);
    return data;
  };

  const signout = () => {
    setLoading(true);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setAuth(null);
    window.location.href = '/';
  };

  const sendVerification = async (email) => {
    if (!email || email === '') return;
    const res = await fetch(`${SERVER_URL}/auth/sendmail`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.message);
    }

    return data;
  };

  const verifyToken = async ({email, code}) => {
    const res = await fetch(`${SERVER_URL}/auth/verify-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, code }),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.message);
    }

    return data;
  };

  const verify = async (id, code) => {
    const res = await fetch(`${SERVER_URL}/auth/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id, code }),
    });
    const data = await res.json();
    const user = JSON.parse(localStorage.getItem('user'));
    user.verified = true;
    localStorage.setItem('user', JSON.stringify(user));
    return data;
  };

  const sendResetMail = async (email) => {
    const res = await fetch(`${SERVER_URL}/auth/passmail`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });
    const data = await res.json();
    return data;
  };

  const reset = async (id, token, password) => {
    const res = await fetch(`${SERVER_URL}/auth/reset`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id, token, password }),
    });
    const data = await res.json();
    return data;
  };

  const values = {
    auth,
    loading,
    signin,
    signup,
    signout,
    sendVerification,
    verifyToken,
    verify,
    checkUsername,
    checkEmail,
    sendResetMail,
    checkPhone,
    reset,
  };

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>;
};

export const useAuth = () => React.useContext(AuthContext);

export default AuthContext;
